import {useQuery} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {HomeService} from 'client';
import {isWithinInterval} from 'date-fns';
import {useAtom, useAtomValue} from 'jotai';
import {useMemo} from 'react';
import {useSearchParams} from 'react-router';
import {getService} from 'utilities';

import {bannerDismissedAtom} from './bannerDismissed';
import {BANNER_CONFIGS} from './Banners';
import {BannerView} from './BannerView';
import {HomeManagedBanner} from './HomeManagedBanner';

const focusDayStart = new Date(2025, 10, 26);
const focusDayEnd = new Date(2025, 10, 27);

const hackWeekStart = new Date(2025, 6, 21);
const hackWeekEnd = new Date(2025, 6, 26);

const osnameStart = new Date('2026-10-20T15:00:00-04:00');
const osnameEnd = new Date('2026-10-23T15:00:00-04:00');

/**
 * The banner above the home dashboard: managed banners behind the GrowthBook
 * flag, otherwise the calendar-driven ones. Turning the flag off rolls back.
 */
export const Banner = () => {
  const {isManagedHomeBannersEnabled} = useAtomValue(growthbookCacheAtom);
  return isManagedHomeBannersEnabled ? <HomeManagedBanner /> : <CalendarBanner />;
};

const CalendarBanner = () => {
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const [dismissed, setDismissed] = useAtom(bannerDismissedAtom);

  const [searchParams] = useSearchParams();

  const forceBanner = searchParams.get('banner');

  const {data} = useQuery({
    queryKey: ['home', 'banner'],
    queryFn: getService(HomeService).bannerApiV1HomeBannerGet,
  });

  const isFocusDay = useMemo(() => {
    return isWithinInterval(new Date(), {start: focusDayStart, end: focusDayEnd});
  }, []);

  const isHackWeek = useMemo(() => {
    return isWithinInterval(new Date(), {start: hackWeekStart, end: hackWeekEnd});
  }, []);

  const isOsnameWindow = useMemo(() => {
    return isWithinInterval(new Date(), {start: osnameStart, end: osnameEnd});
  }, []);

  const fallbackType = isFocusDay ? 'focus' : isHackWeek ? 'hw2025' : undefined;

  // The rename call-out overrides calendar-driven banners (e.g. focus day) while it runs.
  const bannerConfig = {
    ...BANNER_CONFIGS,
  }[
    (forceBanner?.split('-')?.[0] ||
      (isOsnameWindow ? 'osname' : undefined) ||
      data?.company_os_type ||
      fallbackType) as keyof typeof BANNER_CONFIGS
  ];

  if (forceBanner && forceBanner?.split('-')?.[1]) {
    bannerConfig.force = forceBanner?.split('-')?.[1] as 'before' | 'after' | 'live';
  }

  // 9 am PST on 3/25/2025
  const eventDate = new Date('2025-04-22T09:00:00-07:00');

  if (!bannerConfig || (bannerConfig.excludeContractor && employee.type === 'Contractor')) {
    return null;
  }

  // Respect a prior dismissal (per banner type), but always show when a banner
  // is force-previewed via `?banner=` so it stays testable.
  if (!forceBanner && bannerConfig.dismissible && dismissed[bannerConfig.type]) {
    return null;
  }

  const dismiss =
    !forceBanner && bannerConfig.dismissible
      ? () => {
          analyticsLogger().logEvent('HOME_BANNER_CLICKED', {
            action: 'dismiss',
            type: bannerConfig.type,
          });
          setDismissed((prev) => ({...prev, [bannerConfig.type]: true}));
        }
      : undefined;

  return (
    <BannerView
      title={bannerConfig.title}
      subtitle={bannerConfig.getSubtitle(eventDate)}
      marquee={
        bannerConfig.type !== 'soapbox'
          ? {text: bannerConfig.getMarquee(eventDate), eventStart: eventDate}
          : undefined
      }
      background={bannerConfig.background}
      image={bannerConfig.image}
      imageBackground={bannerConfig.imageBackground}
      imageNode={bannerConfig.imageNode}
      onDismiss={dismiss}
    />
  );
};
